import { useQuery } from '@apollo/client';
import Dropdown from 'components/form/Dropdown';
import Input from 'components/form/Input';
import { ToggleView } from 'components/general';
import type { ChannelIntegrationEditorProps } from 'components/integrations/ChannelIntegrationEditor/ChannelIntegrationEditorProps';
import { Alert, AlertType } from 'components/lds';
import { Tooltip } from 'design-system-web/components/Tooltip/Tooltip';
import { getBuildingTenantChannels } from 'lane-shared/graphql/query';
import { convertToUUID } from 'uuid-encoding';
import {
  AccessControlServiceDefinitions,
  AccessControlServiceEntity,
  IntegrationPropertyType,
} from 'lane-shared/helpers/integrations/AccessManagement/accessControl';
import { useFlag } from 'lane-shared/hooks';
import { Channel } from 'packages/lane-shared/types/ChannelType';
import { FeatureFlag } from 'constants-flags';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import rightArrow from '../../../../static/img/right-arrow.svg';
import styles from './TenantMapping.scss';
import { useEntityMappings } from './useEntityMappings';

type PropertyProps = {
  property: IntegrationPropertyType;
  value: any;
  label?: string;
  onChange: (value: any) => void;
  keys?: string[];
  channel: Channel;
  settings: ChannelIntegrationEditorProps['channelIntegration']['settings'];
  metadataLoading?: boolean;
  forCreate?: boolean;
  tenantKey: 'geneaTenantId' | 'acsTenantId';
  acsProviderValue: string;
};

type BuildingTenant = {
  label: string;
  value: any;
};

type TenantMappingPayload = {
  vtsTenantId: any;
  [key: string]: any;
  prevIgsValue?: string;
};

const integrationWithACG: string[] = ['GENEA_2_0'];

export const TenantMapping = ({
  property,
  label,
  onChange,
  channel,
  settings,
  metadataLoading,
  tenantKey,
  acsProviderValue,
}: PropertyProps) => {
  const {
    data: buildingTenants,
  }: {
    data?: { getBuildingTenantChannels: any };
  } = useQuery(getBuildingTenantChannels, {
    variables: { channelId: convertToUUID(channel._id) },
  });

  const { t } = useTranslation();

  const accessGroupIDsDetails =
    AccessControlServiceDefinitions[
      acsProviderValue as AccessControlServiceEntity
    ]?.visitorAccessGroupMappings?.accessGroupIDs;

  const [buildingTenantList, setBuildingTenantList] = useState<any>([]);
  const [IgsTenantlist, setIgsTenantList] = useState([]);
  const [originalTenantValues, setOriginalTenantValues] = useState<
    Record<string, string>
  >({});

  const acgEnhancementEnabled = useFlag(
    FeatureFlag.TenantConfigurationEnhancements,
    false
  );

  const enableTenantMapping = useFlag(FeatureFlag.EnableTenantMapping, false);

  // Memoize channelId to prevent unnecessary re-calls
  const channelId = useMemo(() => convertToUUID(channel._id), [channel._id]);

  // Always call the hook, but skip the query conditionally
  const { entityMappings: entityMappingsFromQuery } = useEntityMappings({
    channelId,
    enabled: enableTenantMapping,
  });

  // Local state for entityMappings to allow immediate updates
  const [entityMappings, setEntityMappings] = useState(
    entityMappingsFromQuery || []
  );

  useEffect(() => {
    if (!metadataLoading) {
      setIgsTenantList(property?.[tenantKey]);
    }
  }, [metadataLoading, settings.locationUuid, property?.[tenantKey]]);

  function getVtsTenantACGMapping(IGSValue: string) {
    const vtsTenantMapACGs = settings?.visitorAccessGroupMappings?.reduce(
      (accumulator: string[], visitorAccessGroupMapping: any) => {
        if (visitorAccessGroupMapping?.geneaTenantIDs?.includes(IGSValue)) {
          accumulator.push(...visitorAccessGroupMapping.accessGroupIDs);
        }

        return accumulator;
      },
      []
    );

    return vtsTenantMapACGs;
  }

  useMemo(() => {
    if (buildingTenants?.getBuildingTenantChannels && !metadataLoading) {
      const buildingTenantList = buildingTenants?.getBuildingTenantChannels
        ?.map((location: { channelId: string; name: string }) => {
          let IGSValue = '';

          if (enableTenantMapping) {
            const tenantMapping = entityMappings?.find(
              item =>
                !!item?.vtsEntityId &&
                convertToUUID(item.vtsEntityId) ===
                  convertToUUID(location.channelId)
            );

            IGSValue = tenantMapping?.thirdPartyEntityId || '';
          } else {
            IGSValue =
              settings?.tenantMappings?.find(
                (item: { vtsTenantId: string }) =>
                  convertToUUID(item.vtsTenantId) ===
                  convertToUUID(location.channelId)
              )?.[tenantKey] || '';
          }

          const vtsTenantMapACGs = getVtsTenantACGMapping(IGSValue);
          const vtsTenantMapACGDetails =
            accessGroupIDsDetails?.filter(
              (acgDetail: { label: string; value: string }) => {
                return vtsTenantMapACGs?.includes(acgDetail?.value);
              }
            ) || [];

          return {
            label: `${location.name}`,
            value: convertToUUID(location.channelId),
            IGSValue,
            mappedTenantACGDetails: [...vtsTenantMapACGDetails],
          };
        })
        .sort((a: BuildingTenant, b: BuildingTenant) =>
          acgEnhancementEnabled ? a.label.localeCompare(b.label) : 0
        );

      setBuildingTenantList(buildingTenantList);
    }
  }, [
    metadataLoading,
    property?.[tenantKey],
    settings?.tenantMappings,
    settings?.visitorAccessGroupMappings,
    buildingTenants,
  ]);

  // Ensure new tenants are added to originalTenantValues
  useEffect(() => {
    if (buildingTenantList.length > 0) {
      setOriginalTenantValues(prevOriginalValues => {
        const originalValues: Record<string, string> = { ...prevOriginalValues };
        let hasNewTenants = false;
        
        buildingTenantList.forEach((tenant: any) => {
          // Check if this is a new tenant
          if (!(tenant.value in originalValues)) {
            hasNewTenants = true;
            originalValues[tenant.value] = tenant.IGSValue;
          }
        });
        
        // Only return new state if there are new tenants
        return hasNewTenants ? originalValues : prevOriginalValues;
      });
    }
  }, [buildingTenantList]);

  const handleChange = (
    nextLaneTenant: {
      label?: string;
      value: any;
      isDisabled?: boolean | undefined;
    },
    id: number
  ) => {
    if (buildingTenantList[id]?.IGSValue !== nextLaneTenant?.value) {
      const updatedTenantList = [...buildingTenantList];

      updatedTenantList[id].IGSValue = nextLaneTenant?.value;
      setBuildingTenantList(updatedTenantList);

      const TenantPayload: TenantMappingPayload[] = updatedTenantList
        ?.filter(item => item?.IGSValue)
        .map(item => {
          if (acgEnhancementEnabled || enableTenantMapping) {
            const originalValue = originalTenantValues[item.value];
            const hasChanged = originalValue !== item.IGSValue;

            return {
              vtsTenantId: item?.value ? convertToUUID(item.value) : item?.value,
              [tenantKey]: item.IGSValue,
              ...(hasChanged && {
                prevIgsValue: originalValue,
              }),
            };
          }

          return {
            vtsTenantId: item?.value ? convertToUUID(item.value) : item?.value,
            [tenantKey]: item.IGSValue,
          };
        });

      onChange(TenantPayload);

      // Update entity mappings when using new tenant mapping system
      if (enableTenantMapping) {
        const updatedEntityMappings = [...entityMappings];

        TenantPayload.forEach((tenantItem: TenantMappingPayload) => {
          const existingIndex = updatedEntityMappings.findIndex(
            mapping => mapping.vtsEntityId === tenantItem.vtsTenantId
          );

          if (existingIndex >= 0) {
            // Update existing mapping - only change thirdPartyEntityId
            updatedEntityMappings[existingIndex] = {
              ...updatedEntityMappings[existingIndex],
              thirdPartyEntityId: tenantItem[tenantKey],
            };
          } else {
            // Add new mapping
            updatedEntityMappings.push({
              vtsEntityId: tenantItem.vtsTenantId,
              thirdPartyEntityId: tenantItem[tenantKey],
              integratedEntityType: 'INTEGRATED_ENTITY_TYPE_TENANT',
            });
          }
        });

        setEntityMappings(updatedEntityMappings);
      }
    }
  };

  const renderCollapseView = () => {
    return (
      <div>
        <div className={styles.parentDescription}>
          Map this property’s tenants to tenant records in the Access Control
          System to accurately enable visitors credentials.
        </div>
        <div className={styles.count}>
          {buildingTenantList?.filter(
            (item: { IGSValue: string }) => item?.IGSValue
          ).length || 0}{' '}
          Tenants mapped
        </div>
      </div>
    );
  };

  const [unmappedTenantsACGExist, setUnmappedTenantsACGExist] = useState(false);

  useMemo(() => {
    if (
      integrationWithACG.includes(acsProviderValue) &&
      (acgEnhancementEnabled || enableTenantMapping)
    ) {
      setUnmappedTenantsACGExist(
        !buildingTenantList?.every(
          (item: { mappedTenantACGDetails: any }) =>
            item?.mappedTenantACGDetails?.length > 0
        )
      );
    }
  }, [buildingTenantList]);

  return (
    <div className={styles.tenantMapping}>
      <div className={styles.mappingBox}>
        <ToggleView
          testId="TenantMapping"
          title={<div className={styles.parentTitle}>{label}</div>}
          collapsedViewContent={renderCollapseView()}
          defaultExpanded
          className={styles.customToggel}
        >
          <div className={styles.parentDescription}>
            {t(
              'web.admin.channel.integrations.access.access-control-settings.tenant-mapping-subtitle'
            )}
          </div>
          {unmappedTenantsACGExist && acgEnhancementEnabled && (
            <Alert type={AlertType.warning} fullWidth>
              {t(
                'web.admin.channel.integrations.access.access-control-settings.unmapped-tenant-exists.warn'
              )}
            </Alert>
          )}
          {buildingTenantList?.map(
            (
              item: {
                value: string;
                IGSValue: any;
                label: string;
                mappedTenantACGDetails: any;
              },
              index: number
            ) => {
              return (
                <div className={styles.flexItem} key={index}>
                  <div className={styles.mappedContent}>
                    <div className={styles.dropdownLabel}>
                      Map this tenant...
                    </div>
                    {acgEnhancementEnabled &&
                    item?.mappedTenantACGDetails?.length > 0 ? (
                      <Tooltip
                        TooltipComponent={
                          <TooltipMessage
                            mappedTenantACGDetails={
                              item?.mappedTenantACGDetails
                            }
                          />
                        }
                        placement="bottom"
                      >
                        <Input
                          disabled
                          value={item?.label}
                          onChange={() => null}
                          showClear={false}
                        />
                      </Tooltip>
                    ) : (
                      <Input
                        disabled
                        showWarning={
                          acgEnhancementEnabled &&
                          integrationWithACG.includes(acsProviderValue)
                        }
                        value={item?.label}
                        onChange={() => null}
                        showClear={false}
                      />
                    )}
                  </div>
                  <img
                    src={rightArrow as string}
                    alt="rightArrow"
                    className={styles.rightArrow}
                  />
                  <div className={styles.mappedContent}>
                    <div className={styles.dropdownLabel}>with this one</div>
                    <Dropdown
                      items={IgsTenantlist}
                      value={item?.IGSValue || ''}
                      onChange={value => handleChange(value, index)}
                      invalid={
                        acgEnhancementEnabled &&
                        item?.IGSValue?.trim()?.length === 0
                      }
                      errors={
                        acgEnhancementEnabled &&
                        item?.IGSValue?.trim()?.length === 0
                          ? [
                              t(
                                'web.admin.channel.integrations.access.access-control-settings.missing-mapping'
                              ),
                            ]
                          : []
                      }
                      isFullWidth
                      placeholder="Not mapped"
                      testId={`igs-tenant-${index}`}
                    />
                  </div>
                </div>
              );
            }
          )}
        </ToggleView>
      </div>
    </div>
  );
};

const TooltipMessage = (props: any) => {
  const { t } = useTranslation();
  const message = useMemo(() => {
    let acgsNames = '';

    props?.mappedTenantACGDetails?.forEach((acg: { label: string }) => {
      acgsNames += `${acg?.label}, `;
    });
    acgsNames = acgsNames.slice(0, -2);

    return acgsNames;
  }, [props?.mappedTenantACGDetails]);

  return (
    <>
      {t(
        'web.admin.channel.integrations.access.access-control-settings.tenant-mapping-tooltip',
        {
          count: props?.mappedTenantACGDetails?.length,
          message,
        }
      )}
    </>
  );
};
